import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";

// 모달 컴포넌트
function ItineraryModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full md:w-[680px] md:max-h-[85vh] rounded-t-3xl md:rounded-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 이미지 */}
        <div
          className="relative h-52 md:h-64 shrink-0"
          style={{
            backgroundImage: `url(${item.img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            className="absolute inset-0 rounded-t-3xl md:rounded-t-2xl"
            style={{ background: `linear-gradient(to top, ${item.color}cc, transparent)` }}
          />
          {/* 닫기 버튼 */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-lg"
          >
            ✕
          </button>
          <div className="absolute bottom-0 left-0 p-6 text-white" style={{ background: `linear-gradient(to top, ${item.overlayColor}ee, transparent)` }}>
            <span className="text-[11px] tracking-widest uppercase opacity-70">{item.tag} — {item.sub}</span>
            <h2 className="text-[26px] font-bold mt-1">{item.title}</h2>
          </div>
        </div>

        {/* 본문 */}
        <div className="p-6">
          <p className="text-gray-500 text-[14px] mb-6 leading-relaxed">{item.desc}</p>

          {/* 일정 리스트 */}
          <div className="flex flex-col gap-4">
            {item.itinerary.map((day, i) => (
              <div key={i} className="flex gap-4 items-start">
                {/* 사진 */}
                <div
                  className="shrink-0 w-20 h-20 rounded-xl"
                  style={{
                    backgroundImage: `url(${day.img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                />
                {/* 텍스트 */}
                <div className="flex flex-col gap-1 justify-start items-start">
                  <span
                    className="text-[11px] font-bold tracking-widest uppercase"
                    style={{ color: item.color }}
                  >
                    {day.day}
                  </span>
                  <h3 className="text-[15px] font-bold text-black">{day.title}</h3>
                  <p className="text-[13px] text-gray-500 leading-relaxed">{day.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Itineraries() {
  const { t } = useTranslation();

  const categories = t("categories", {
    returnObjects: true,
  });

  const leftFadeRef = useRef(null);
  const rightFadeRef = useRef(null);

  const [selectedItem, setSelectedItem] = useState(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
    skipSnaps: false,
  });

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  useEffect(() => {
    if (!emblaApi) return;

    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };

    emblaApi.on("select", update);
    emblaApi.on("reInit", update);

    update();

    return () => {
      emblaApi.off("select", update);
    };
  }, [emblaApi]);

  return (
    <div className="relative py-10 px-[10vw] md:px-[10px] bg-[white]">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4 py-6">
          {categories.map((item, index) => (
            <div
              key={index}
              className="shrink-0"
              style={{ flex: "0 0 300px" }}
            >
              <ItineraryCard
                item={item}
                onClick={() => setSelectedItem(item)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* 우측 힌트 */}
      <div
        ref={rightFadeRef}
        className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
        style={{
          opacity: canNext ? 1 : 0,
          transition: "0.3s"
        }}
        onClick={() => emblaApi?.scrollNext()}
      >
        <span className="w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-lg cursor-pointer hover:bg-black/60 transition">
          ›
        </span>
      </div>

      {/* 좌측 힌트 */}
      <div
        ref={leftFadeRef}
        className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
        style={{
          opacity: canPrev ? 1 : 0,
          transition: "0.3s"
        }}
        onClick={() => emblaApi?.scrollPrev()}
      >
        <span className="w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-lg cursor-pointer hover:bg-black/60 transition">
          ‹
        </span>
      </div>

      {/* 모달 */}
      {selectedItem && (
        <ItineraryModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}

function ItineraryCard({ item, onClick }) {
  const { t } = useTranslation();
  return (
    <div
      className="shrink-0 rounded-2xl overflow-hidden bg-white cursor-pointer"
      style={{
        width: "300px",
        scrollSnapAlign: "start",
        boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
      }}
      onClick={onClick}
    >
      {/* 사진 */}
      <div
        className="w-full"
        style={{
          height: "200px",
          backgroundImage: `url(${item.img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* 텍스트 — 높이 고정 */}
      <div
        className="p-4 flex flex-col justify-between"
        style={{ height: "180px" }}
      >
        <div className="flex flex-col gap-1 overflow-hidden">
          <span className="text-[12px] font-bold" style={{ color: item.color }}>
            {item.sub}
          </span>
          <h3
            className="text-[16px] font-bold text-black leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.title}
          </h3>
          <p
            className="text-[13px] text-gray-500 leading-relaxed"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.desc}
          </p>
        </div>

        {/* 추천 시즌 — 항상 하단 고정 */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <span className="text-[12px] text-gray-400">{t("common.bestSeason")}</span>
          <span className="text-[12px] font-bold" style={{ color: item.color }}>
            {item.season}
          </span>
        </div>
      </div>
    </div>
  );
}